import React, { useContext, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";

import {
  RefinementList,
  NumericMenu,
  DynamicWidgets,
  connectRefinementList,
} from "react-instantsearch-dom";

import { AuthContext, JobsContext, SearchContext } from "context/providers";

import {
  DEFAULT_BH_STATUSES_TO_EXCLUDE,
  DEFAULT_RATE_RANGE,
  EDUCATION_DEGREE_TYPES,
  EDUCATION_DEGREE_TYPES_TEXT,
  LANGUAGE_KEY_NAMES,
  USER_TYPES,
  YEARS_OF_EXPERIENCE_CONFIG,
  YEARS_OF_EXPERIENCE_CONFIG_IS_ENABLED,
} from "lookup";

import {
  CustomRangeSlider,
  CustomRefinementList,
  CollapsiblePanel,
  CollapsibleCustomPanel,
  // PlacementsRefinementWrapper,  // replaced by BHStatusFilterWrapper (bullhornStatus)
} from "./components";
import HideUsersFilter from "./components/HideUsersFilter";
import FilterToggleRefinement from "./components/FilterToggleRefinement";
import TextSearchFilter from "./components/TextSearchFilter";
import LocationFiltersWrapper from "components/LocationsFilterWrapper";
import YearsOfExperience from "./components/YearsOfExperience";
import BHStatusFilterWrapper from "./components/BHStatusFilterWrapper";
import HierarchicalSkillFilter from "./components/HierarchicalSkillFilter";
import CommunityBoost from "./components/CommunityBoost";
import { cn } from "utils";

const subtractUnix = (amount, unit) =>
  dayjs().subtract(amount, unit).unix() * 1000;

const CREATED_AT_ITEMS = [
  {
    label: "< 1 day",
    start: subtractUnix(1, "days"),
    end: dayjs().unix() * 1000,
  },
  {
    label: "< 7 days",
    start: subtractUnix(6, "days"),
    end: dayjs().unix() * 1000,
  },
  {
    label: "< 30 days",
    start: subtractUnix(29, "days"),
    end: dayjs().unix() * 1000,
  },
];

const LAST_ACTIVITY_ITEMS = [
  {
    label: "< 1 day",
    start: subtractUnix(1, "days"),
    end: dayjs().unix() * 1000,
  },
  {
    label: "< 7 days",
    start: subtractUnix(6, "days"),
    end: dayjs().unix() * 1000,
  },
  {
    label: "< 30 days",
    start: subtractUnix(29, "days"),
    end: dayjs().unix() * 1000,
  },
  {
    label: "< 60 days",
    start: subtractUnix(59, "days"),
    end: dayjs().unix() * 1000,
  },
  {
    label: "> 60 days",
    end: dayjs().unix() * 1000,
  },
];

const isCountryAllowedForYearsOfExperience = (countryName) => {
  if (!YEARS_OF_EXPERIENCE_CONFIG_IS_ENABLED) {
    return false;
  }

  return YEARS_OF_EXPERIENCE_CONFIG.locationCountryNames.includes(countryName);
};

const VirtualRefinementList = connectRefinementList(() => null);

const FacetWidgetSlot = ({ attributes, children }) => <>{children}</>;

const FILTER_ATTRIBUTE_ORDER = [
  "lastActivityDate_dt",
  "careers.TKSkillsHierarchy",
  "intelProfessions.name",
  "ratePerHour.value",
  "availability",
  "createdAt_dt",
  "educationRecords.degreeType",
  "assessments.testName",
  "assessments.normalizedScore",
  "assessments.resultDetails.integrityReport.risk",
  "workAuthorizations",
  "noticePeriod",
  "bullhornStatus",
  "knownLanguages.language",
  "knownLanguages.level",
  "knownLanguages_str",
  "cognitoGroups",
  "talentSource",
  "referrerCode",
  "referrerSource",
  "userType",
  "agreedToMarketing",
];

const dynamicWidgetsTransformItems = () => FILTER_ATTRIBUTE_ORDER;

const DYNAMIC_WIDGETS_PROPS = {
  facets: ["*"],
  maxValuesPerFacet: 500,
  transformItems: dynamicWidgetsTransformItems,
};

const SearchFilters = ({ indexName }) => {
  const { searchState, operators, setOperators, geographicalRegions } =
    useContext(SearchContext);
  const { jobOpp } = useContext(JobsContext);
  const { user: loggedInUser } = useContext(AuthContext);

  const [showRequiredExperience, setShowRequiredExperience] = useState(false);

  const mapToFacetString = (firstAttributes, secondAttributes) => {
    const output = [];

    firstAttributes.forEach((firstAttribute) => {
      secondAttributes.forEach((secondAttribute) => {
        output.push(`${firstAttribute}_${secondAttribute}`);
      });
    });

    return output;
  };

  const languagesStr = useMemo(() => {
    const languages =
      searchState.refinementList?.[LANGUAGE_KEY_NAMES.language] || [];
    const languagesLevel =
      searchState.refinementList?.[LANGUAGE_KEY_NAMES.level] || [];

    return mapToFacetString(languages, languagesLevel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchState.refinementList]);

  const definedRate = useMemo(
    () => searchState?.range?.["ratePerHour.value"],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchState?.range?.["ratePerHour.value"]]
  );

  const handleChange = (value, valueKey, targetName) => {
    switch (targetName) {
      case "operators": {
        setOperators(valueKey);
        break;
      }

      default: {
        break;
      }
    }
  };

  useEffect(() => {
    if (jobOpp?.location?.countryName) {
      // Country should be in the whitelist
      if (isCountryAllowedForYearsOfExperience(jobOpp.location?.countryName)) {
        setShowRequiredExperience(true);
      } else {
        setShowRequiredExperience(false);
      }
    } else if (jobOpp?.geographicalRegions?.length > 0) {
      // ALL countries should be in the whitelist
      const countries = jobOpp.geographicalRegions
        .map((grOuter) => {
          if (grOuter.countryNames.length === 0) {
            const matchingRegion = geographicalRegions?.find(
              (grInner) => grInner.regionName === grOuter.regionName
            );
            return matchingRegion?.countryNames || [];
          }

          return [...grOuter.countryNames];
        })
        .flat();

      if (countries.every(isCountryAllowedForYearsOfExperience)) {
        setShowRequiredExperience(true);
      } else {
        setShowRequiredExperience(false);
      }
    } else {
      setShowRequiredExperience(false);
    }
  }, [jobOpp, geographicalRegions]);

  return (
    <>
      <TextSearchFilter />

      <div id="filter-group-location">
        <LocationFiltersWrapper />
        <VirtualRefinementList attribute="location.stateName" />
        <VirtualRefinementList attribute="location.cityName" />
      </div>

      <DynamicWidgets {...DYNAMIC_WIDGETS_PROPS}>
        <FacetWidgetSlot attributes={["lastActivityDate_dt"]}>
          <div id="filter-group-last-active">
            <CollapsiblePanel
              header="Last activity date"
              sectionKey="last-activity"
              defaultCollapsed={true}
            >
              <NumericMenu
                attribute="lastActivityDate_dt"
                items={LAST_ACTIVITY_ITEMS}
                transformItems={(items) => {
                  return items.map((item) => {
                    return {
                      ...item,
                      noRefinement: false,
                    };
                  });
                }}
              />
            </CollapsiblePanel>
          </div>
        </FacetWidgetSlot>

        <FacetWidgetSlot attributes={["careers.TKSkillsHierarchy"]}>
          <CollapsibleCustomPanel
            title="Skills"
            sectionKey="skills"
            defaultCollapsed={true}
            helpText="If you would like to filter multiple skills. Ex:  C# and Java. Select OR if you want to see talent with either of the skill. Select AND if you would like to see the only talents with both the skills."
            checked={operators["TKSkillsHierarchy"] === "and"}
            valueKey="TKSkillsHierarchy"
            leftToggleLabel="OR"
            rightToggleLabel="AND"
            onChange={(value, valueKey) =>
              handleChange(value, valueKey, "operators")
            }
            toggle
          >
            <HierarchicalSkillFilter
              attribute="careers.TKSkillsHierarchy"
              placeholder="Search for skills"
              limit={15}
              showMoreLimit={500}
              searchable
              operator={operators["TKSkillsHierarchy"]}
              showMore
              TKSkills={jobOpp?.TKSkills}
              optionalTKSkills={jobOpp?.optionalTKSkills}
              TKSkillsHierarchyExp={searchState.TKSkillsHierarchyExp}
              TKSkillsHierarchyExpAttrName="TKSkillsHierarchyExp"
            />
          </CollapsibleCustomPanel>
        </FacetWidgetSlot>

        <FacetWidgetSlot attributes={["intelProfessions.name"]}>
          <CollapsibleCustomPanel
            title="Profession"
            sectionKey="profession"
            defaultCollapsed={true}
            checked={operators["intelProfessions.name"] === "and"}
            valueKey="intelProfessions.name"
            leftToggleLabel="OR"
            rightToggleLabel="AND"
            helpText="Select OR to see talent in any selected profession. Select AND to see only talent whose profile includes every selected profession."
            onChange={(value, valueKey) =>
              handleChange(value, valueKey, "operators")
            }
            toggle
          >
            <CustomRefinementList
              attribute="intelProfessions.name"
              placeholder="Search for professions"
              operator={operators["intelProfessions.name"]}
              limit={15}
              showCount
              searchable
              showMore
            />
          </CollapsibleCustomPanel>
        </FacetWidgetSlot>

        <FacetWidgetSlot attributes={["ratePerHour.value"]}>
          <div id="filter-group-hourly-rate">
            <CollapsiblePanel
              header="Hourly Rate"
              sectionKey="hourly-rate"
              defaultCollapsed={true}
            >
              <CustomRangeSlider
                attribute="ratePerHour.value"
                {...(definedRate?.min !== undefined && {
                  min: DEFAULT_RATE_RANGE.min,
                })}
                {...(definedRate?.max !== undefined && {
                  max: DEFAULT_RATE_RANGE.max,
                })}
                displayMin={DEFAULT_RATE_RANGE.min}
                displayMax={DEFAULT_RATE_RANGE.max}
                showInputs
              />
            </CollapsiblePanel>
          </div>
        </FacetWidgetSlot>

        <FacetWidgetSlot attributes={["availability"]}>
          <div id="filter-group-availability">
            <CollapsiblePanel
              header="Availability"
              sectionKey="availability"
              defaultCollapsed={true}
            >
              <RefinementList attribute="availability" showCount />
            </CollapsiblePanel>
          </div>
        </FacetWidgetSlot>

        <FacetWidgetSlot attributes={["createdAt_dt"]}>
          <div id="filter-group-sign-up-date">
            <CollapsiblePanel
              header="Sign Up Date"
              sectionKey="sign-up-date"
              defaultCollapsed={true}
            >
              <NumericMenu
                attribute="createdAt_dt"
                items={CREATED_AT_ITEMS}
                transformItems={(items) => {
                  return items.map((item) => {
                    return {
                      ...item,
                      noRefinement: false,
                    };
                  });
                }}
              />
            </CollapsiblePanel>
          </div>
        </FacetWidgetSlot>

        <FacetWidgetSlot attributes={["educationRecords.degreeType"]}>
          <CollapsibleCustomPanel
            id="filter-group-education-records"
            title="Education"
            sectionKey="education"
            defaultCollapsed={true}
            checked={operators["educationRecords.degreeType"] === "and"}
            valueKey="educationRecords.degreeType"
            leftToggleLabel="OR"
            rightToggleLabel="AND"
            helpText="If you would like to filter multiple degree types. Ex:  Bachelors and Masters. Select OR if you want to see talent with either of the degree types. Select AND if you would like to see the only talents with both the degree types."
            onChange={(value, valueKey) =>
              handleChange(value, valueKey, "operators")
            }
            toggle
          >
            <RefinementList
              attribute="educationRecords.degreeType"
              placeholder="Search for degree type"
              operator={operators["educationRecords.degreeType"]}
              limit={15}
              searchable
              showMore
              showCount
              transformItems={(items) =>
                items
                  .filter(
                    (e) =>
                      e.label !== EDUCATION_DEGREE_TYPES.DEGREE_TYPE_UNSPECIFIED
                  )
                  .map((item) => ({
                    ...item,
                    label:
                      EDUCATION_DEGREE_TYPES_TEXT[item.label] ?? item.label,
                  }))
              }
            />
          </CollapsibleCustomPanel>
        </FacetWidgetSlot>

        <FacetWidgetSlot attributes={["assessments.testName"]}>
          <CollapsibleCustomPanel
            id="filter-group-test-name"
            title="Test Name"
            sectionKey="test-name"
            defaultCollapsed={true}
            checked={operators["assessments.testName"] === "and"}
            valueKey="assessments.testName"
            leftToggleLabel="OR"
            rightToggleLabel="AND"
            helpText="If you would like to filter multiple tests. Ex:  C# and Java. Select OR if you want to see talent with either of the test. Select AND if you would like to see the only talents with both the tests."
            onChange={(value, valueKey) =>
              handleChange(value, valueKey, "operators")
            }
            toggle
          >
            <CustomRefinementList
              attribute="assessments.testName"
              placeholder="Search for tests"
              operator={operators["assessments.testName"]}
              limit={15}
              showCount
              searchable
              showMore
              showChip
            />
          </CollapsibleCustomPanel>
        </FacetWidgetSlot>

        <FacetWidgetSlot attributes={["assessments.normalizedScore"]}>
          <div id="filter-group-final-score">
            <CollapsiblePanel
              header="Assessments Final Score"
              sectionKey="assessments"
              defaultCollapsed={true}
            >
              <NumericMenu
                attribute="assessments.normalizedScore"
                items={[
                  { label: "<= 25%", start: 0, end: 25 },
                  { label: "26% - 50%", start: 26, end: 50 },
                  { label: "51% - 75%", start: 51, end: 75 },
                  { label: ">= 76%", start: 76, end: 100 },
                  { label: "= 100%", start: 100, end: 100 },
                ]}
                transformItems={(items) =>
                  items.map((item) => {
                    return {
                      ...item,
                      noRefinement: false,
                    };
                  })
                }
              />
            </CollapsiblePanel>
          </div>
        </FacetWidgetSlot>

        <FacetWidgetSlot
          attributes={["assessments.resultDetails.integrityReport.risk"]}
        >
          <div id="filter-group-integrity-risk-level">
            <CollapsiblePanel
              header="Assessments Integrity Risk Level"
              sectionKey="integrity-risk-level"
              defaultCollapsed={true}
            >
              <RefinementList
                attribute="assessments.resultDetails.integrityReport.risk"
                transformItems={(items) =>
                  items.map((item) => ({
                    ...item,
                    label:
                      item.label.charAt(0).toUpperCase() + item.label.slice(1),
                  }))
                }
              />
            </CollapsiblePanel>
          </div>
        </FacetWidgetSlot>
      </DynamicWidgets>

      {showRequiredExperience && <YearsOfExperience />}

      <DynamicWidgets {...DYNAMIC_WIDGETS_PROPS}>
        <FacetWidgetSlot attributes={["workAuthorizations"]}>
          <div id="filter-group-work-authorization">
            <CollapsiblePanel
              header="Work Authorization"
              sectionKey="work-auth"
              defaultCollapsed={true}
            >
              <RefinementList attribute="workAuthorizations" showCount />
            </CollapsiblePanel>
          </div>
        </FacetWidgetSlot>

        <FacetWidgetSlot attributes={["noticePeriod"]}>
          <div id="filter-group-notice-period">
            <CollapsiblePanel
              header="Notice Period"
              sectionKey="notice-period"
              defaultCollapsed={true}
            >
              <NumericMenu
                attribute="noticePeriod"
                items={[
                  { label: "Immediate", start: 0, end: 0 },
                  { label: "<= 15 Days", end: 15 },
                  { label: "<= 30 Days", end: 30 },
                  { label: "<= 60 Days", end: 60 },
                  { label: "<= 90 Days", end: 90 },
                ]}
                translations={{
                  all: "All",
                }}
                transformItems={(items) =>
                  items.map((item) => {
                    return {
                      ...item,
                      noRefinement: false,
                    };
                  })
                }
              />
            </CollapsiblePanel>
          </div>
        </FacetWidgetSlot>

        <FacetWidgetSlot attributes={["bullhornStatus"]}>
          <div id="filter-group-bh-status">
            <BHStatusFilterWrapper
              isReadOnly={
                jobOpp?.id && DEFAULT_BH_STATUSES_TO_EXCLUDE?.length > 0
              }
            />
          </div>
        </FacetWidgetSlot>
      </DynamicWidgets>

      {jobOpp?.id && <HideUsersFilter />}

      <DynamicWidgets {...DYNAMIC_WIDGETS_PROPS}>
        <FacetWidgetSlot attributes={["knownLanguages.language"]}>
          <CollapsibleCustomPanel
            title="Language"
            sectionKey="language"
            defaultCollapsed={true}
            checked={operators["knownLanguages.language"] === "and"}
            valueKey="knownLanguages.language"
            leftToggleLabel="OR"
            rightToggleLabel="AND"
            helpText="If you would like to filter multiple languages. Ex:  English and Spanish. Select OR if you want to see talent with either of the language. Select AND if you would like to see the only talents with both the language."
            onChange={(value, valueKey) =>
              handleChange(value, valueKey, "operators")
            }
            toggle
          >
            <CustomRefinementList
              attribute="knownLanguages.language"
              translations={{
                placeholder: "Search for languages",
              }}
              operator={operators["knownLanguages.language"]}
              limit={5}
              showMoreLimit={500}
              showCount
              showMore
              searchable
            />
          </CollapsibleCustomPanel>
        </FacetWidgetSlot>

        <FacetWidgetSlot attributes={["knownLanguages.level"]}>
          <CollapsiblePanel
            header="Language Level"
            sectionKey="lang-level"
            defaultCollapsed={true}
          >
            <CustomRefinementList
              attribute="knownLanguages.level"
              placeholder="Search for language level"
              showCount
            />
          </CollapsiblePanel>
        </FacetWidgetSlot>

        <FacetWidgetSlot attributes={["knownLanguages_str"]}>
          <VirtualRefinementList
            attribute="knownLanguages_str"
            defaultRefinement={languagesStr}
          />
        </FacetWidgetSlot>

        <FacetWidgetSlot attributes={["cognitoGroups"]}>
          <CollapsibleCustomPanel
            id="filter-group-community"
            title="Community"
            sectionKey="community"
            defaultCollapsed={true}
            checked={operators["cognitoGroups"] === "and"}
            valueKey="cognitoGroups"
            leftToggleLabel="OR"
            rightToggleLabel="AND"
            helpText="Select OR if you want to see talent within ANY of these groups. Select AND if you would like to only see talent within ALL of the selected groups."
            onChange={(value, valueKey) =>
              handleChange(value, valueKey, "operators")
            }
            toggle
          >
            <RefinementList
              attribute="cognitoGroups"
              operator={operators["cognitoGroups"]}
              showCount
            />
          </CollapsibleCustomPanel>
        </FacetWidgetSlot>

        {/* filter(Boolean) avoids null/false children — DynamicWidgets crashes on them */}
        {[
          loggedInUser?.canAccessTalentSourceFilter && (
            <FacetWidgetSlot key="talent-source" attributes={["talentSource"]}>
              <div id="filter-group-talent-source">
                <CollapsiblePanel
                  header="Talent source"
                  sectionKey="talent-source"
                  defaultCollapsed={true}
                >
                  <RefinementList attribute="talentSource" showCount />
                </CollapsiblePanel>
              </div>
            </FacetWidgetSlot>
          ),
        ].filter(Boolean)}

        <FacetWidgetSlot attributes={["referrerCode"]}>
          <div id="filter-group-referrer-code">
            <CollapsiblePanel
              header="Referrer Code"
              sectionKey="referrer-code"
              defaultCollapsed={true}
            >
              <RefinementList
                attribute="referrerCode"
                translations={{
                  placeholder: "Search for specific referrer code",
                }}
                limit={5}
                searchable
                showMore
                showCount
              />
            </CollapsiblePanel>
          </div>
        </FacetWidgetSlot>

        {[
          loggedInUser?.canAccessTalentSourceFilter && (
            <FacetWidgetSlot
              key="referrer-source"
              attributes={["referrerSource"]}
            >
              <div id="filter-group-referrer-source">
                <CollapsiblePanel
                  header="Referrer Source"
                  sectionKey="referrer-source"
                  defaultCollapsed={true}
                >
                  <RefinementList
                    attribute="referrerSource"
                    items={[
                      { label: "Recruiter resume", value: "RECRUITERRESUME" },
                    ]}
                    transformItems={(items) =>
                      items.map((item) => {
                        return {
                          ...item,
                          noRefinement: false,
                        };
                      })
                    }
                    showCount
                  />
                </CollapsiblePanel>
              </div>
            </FacetWidgetSlot>
          ),
        ].filter(Boolean)}

        <FacetWidgetSlot attributes={["userType"]}>
          <div id="filter-group-user-type">
            <CollapsiblePanel
              header="User Type"
              sectionKey="user-type"
              defaultCollapsed={true}
            >
              <CustomRefinementList
                attribute="userType"
                predefinedValue={[USER_TYPES.FREELANCER]}
                placeholder="Search for user type"
                showCount
                searchable
                showChip
              />
            </CollapsiblePanel>
          </div>
        </FacetWidgetSlot>

        <FacetWidgetSlot attributes={["agreedToMarketing"]}>
          <div id="filter-group-user-agreements">
            <CollapsiblePanel
              header="User Agreements"
              sectionKey="user-agreements"
              defaultCollapsed={true}
            >
              <div className="flex flex-col gap-3">
                <FilterToggleRefinement
                  attribute="agreedToMarketing"
                  label="Agreed to marketing"
                  value={false}
                  defaultRefinement={false}
                />
                <FilterToggleRefinement
                  attribute="agreedToTerms"
                  label="Agreed to terms"
                  value={true}
                  defaultRefinement={false}
                />
              </div>
            </CollapsiblePanel>
          </div>
        </FacetWidgetSlot>
      </DynamicWidgets>

      {loggedInUser?.canAccessCommunityBoost && (
        <div id="filter-group-community-boost" className="w-full">
          <CollapsiblePanel
            header="Community Boost"
            sectionKey="community-boost"
            defaultCollapsed={true}
            className={cn("whitespace-normal xl:whitespace-nowrap", {
              "opacity-80":
                searchState.sortBy && searchState.sortBy !== indexName,
            })}
          >
            <CommunityBoost
              disabled={searchState.sortBy && searchState.sortBy !== indexName}
            />
          </CollapsiblePanel>
        </div>
      )}
    </>
  );
};

export default SearchFilters;
